import React, { useMemo, useRef, useState, useCallback } from "react";
import { useVehiculesStore } from "@store/vehiculesStore";
import { snapToGrid, formatTime } from "@utils/timeGrid";
import type { Vehicle, VehicleReservation } from "../../types/garage/vehicules";
import ReservationModal from "@components/modals/ReservationModal";
import VehiculeModal from "@components/modals/VehiculeModal";

// --- Constants ---
const HOURS_START = 8;
const HOURS_END = 20;
const TOTAL_HOURS = HOURS_END - HOURS_START; // 12
const SNAP_QUARTER = 0.25;
const ROW_HEIGHT = 40;
const QUARTER_WIDTH = 20;
const HOUR_WIDTH = QUARTER_WIDTH * 4; // 80
const GRID_WIDTH = TOTAL_HOURS * HOUR_WIDTH; // 960
const VEHICLE_COL_WIDTH = 190;

const USAGE_COLORS: Record<string, { dot: string }> = {
  location: { dot: "#6366F1" },
  pret: { dot: "#22C55E" },
  velos: { dot: "#F59E0B" },
};
const HEADER_HEIGHT = 28;

type DayType = "single" | "first" | "middle" | "last";

interface DragState {
  reservationId: string;
  mode: "move" | "resize" | "resize-start";
  dayType: DayType;
  original: VehicleReservation;
  startX: number;
  startY: number;
  /** Delta in hours from drag start — applied differently per dayType */
  deltaHours: number;
  previewVehicleId: string;
}

/** Compute what the dragged values would be */
function applyDrag(drag: DragState) {
  const { original, dayType, mode, deltaHours } = drag;
  let startHour = original.startHour;
  let returnHour = original.returnHour;
  let durationHours = original.durationHours;

  if (mode === "move") {
    // Move always shifts both startHour and returnHour by the same delta
    const dS = snapToGrid(deltaHours, SNAP_QUARTER);
    if (startHour != null) startHour = startHour + dS;
    if (returnHour != null) returnHour = returnHour + dS;
    if (dayType === "single") {
      // For single-day, clamp startHour within valid range
      startHour = Math.max(HOURS_START, Math.min(HOURS_END - (durationHours ?? 1), startHour ?? HOURS_START));
    }
  } else if (mode === "resize") {
    // Right edge → end of the reservation
    if (dayType === "single") {
      // Adjust duration (start fixed)
      const raw = snapToGrid((durationHours ?? 1) + deltaHours, SNAP_QUARTER);
      const max = HOURS_END - (startHour ?? HOURS_START);
      durationHours = Math.max(SNAP_QUARTER, Math.min(max, raw));
    } else if (dayType === "last") {
      // Adjust returnHour on the last day
      const raw = snapToGrid((returnHour ?? HOURS_END) + deltaHours, SNAP_QUARTER);
      returnHour = Math.max(HOURS_START + SNAP_QUARTER, Math.min(HOURS_END, raw));
    }
  } else if (mode === "resize-start") {
    // Left edge → start of the reservation
    if (dayType === "single") {
      // Move the start while keeping the end fixed → duration adjusts
      const end = (startHour ?? HOURS_START) + (durationHours ?? 1);
      const rawStart = snapToGrid((startHour ?? HOURS_START) + deltaHours, SNAP_QUARTER);
      startHour = Math.max(HOURS_START, Math.min(end - SNAP_QUARTER, rawStart));
      durationHours = end - startHour;
    } else if (dayType === "first") {
      // Adjust the first-day start hour (reservation continues to the next day)
      const rawStart = snapToGrid((startHour ?? HOURS_START) + deltaHours, SNAP_QUARTER);
      startHour = Math.max(HOURS_START, Math.min(HOURS_END - SNAP_QUARTER, rawStart));
    }
  }

  return { startHour, returnHour, durationHours, vehicleId: drag.previewVehicleId };
}

export default function DayReservationGrid() {
  const getFilteredVehicles = useVehiculesStore((s) => s.getFilteredVehicles);
  const reservations = useVehiculesStore((s) => s.reservations);
  const selectedDate = useVehiculesStore((s) => s.selectedDate);
  const updateReservation = useVehiculesStore((s) => s.updateReservation);
  const deleteReservation = useVehiculesStore((s) => s.deleteReservation);

  const allVehicles = useVehiculesStore((s) => s.vehicles);
  const updateVehicle = useVehiculesStore((s) => s.updateVehicle);

  const vehicles = getFilteredVehicles();
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [editingReservation, setEditingReservation] = useState<VehicleReservation | null>(null);
  const dragMoved = useRef(false);

  // Reservations visible on the selected date
  const dayReservations = useMemo(() => {
    return reservations.filter((r) => {
      if (r.startDate === r.endDate) return r.startDate === selectedDate;
      return r.startDate <= selectedDate && r.endDate >= selectedDate;
    });
  }, [reservations, selectedDate]);

  // Map reservations to vehicles
  const resByVehicle = useMemo(() => {
    const map: Record<string, VehicleReservation[]> = {};
    for (const v of vehicles) map[v.id] = [];
    for (const r of dayReservations) {
      if (map[r.vehicleId]) map[r.vehicleId].push(r);
    }
    return map;
  }, [vehicles, dayReservations]);

  const hours = useMemo(() => {
    const arr: number[] = [];
    for (let h = HOURS_START; h < HOURS_END; h++) arr.push(h);
    return arr;
  }, []);

  const getDayType = useCallback(
    (r: VehicleReservation): DayType => {
      if (r.startDate === r.endDate) return "single";
      if (selectedDate === r.startDate) return "first";
      if (selectedDate === r.endDate) return "last";
      return "middle";
    },
    [selectedDate],
  );

  // Get bar position in pixels, with optional overrides for preview
  const getBarPx = useCallback(
    (
      r: VehicleReservation,
      overrides?: { startHour?: number; returnHour?: number; durationHours?: number },
    ) => {
      const dayType = getDayType(r);
      const sh = overrides?.startHour ?? r.startHour;
      const rh = overrides?.returnHour ?? r.returnHour;
      const dh = overrides?.durationHours ?? r.durationHours;

      if (dayType === "single") {
        if (sh != null && dh != null) {
          const left = (sh - HOURS_START) * HOUR_WIDTH;
          const width = dh * HOUR_WIDTH;
          return { left: Math.max(0, left), width: Math.max(QUARTER_WIDTH, width) };
        }
        return { left: 0, width: GRID_WIDTH };
      }

      if (dayType === "first" && sh != null) {
        const left = (sh - HOURS_START) * HOUR_WIDTH;
        const width = GRID_WIDTH - left;
        return { left: Math.max(0, left), width: Math.max(QUARTER_WIDTH, width) };
      }

      if (dayType === "last" && rh != null) {
        const width = (rh - HOURS_START) * HOUR_WIDTH;
        return { left: 0, width: Math.max(QUARTER_WIDTH, width) };
      }

      // Middle day or no hour info
      return { left: 0, width: GRID_WIDTH };
    },
    [getDayType],
  );

  const vehicleIndexFromY = useCallback(
    (clientY: number) => {
      if (!containerRef.current) return -1;
      const rect = containerRef.current.getBoundingClientRect();
      const y = clientY - rect.top - HEADER_HEIGHT;
      const idx = Math.floor(y / ROW_HEIGHT);
      return Math.max(0, Math.min(vehicles.length - 1, idx));
    },
    [vehicles.length],
  );

  // --- Drag handlers ---
  const CLICK_THRESHOLD = 5; // px — below this, treat as click not drag

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, r: VehicleReservation, mode: "move" | "resize" | "resize-start") => {
      const dayType = getDayType(r);
      // Middle days with no hour info: click opens modal, no drag
      if (dayType === "middle" && r.startHour == null && r.returnHour == null) {
        setEditingReservation(r);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      dragMoved.current = false;
      setDrag({
        reservationId: r.id,
        mode,
        dayType,
        original: { ...r },
        startX: e.clientX,
        startY: e.clientY,
        deltaHours: 0,
        previewVehicleId: r.vehicleId,
      });
    },
    [getDayType],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!drag) return;
      const deltaX = e.clientX - drag.startX;
      const deltaY = e.clientY - drag.startY;
      if (Math.abs(deltaX) > CLICK_THRESHOLD || Math.abs(deltaY) > CLICK_THRESHOLD) {
        dragMoved.current = true;
      }
      const deltaHours = deltaX / HOUR_WIDTH;
      const vIdx = vehicleIndexFromY(e.clientY);
      const targetVehicle = vehicles[vIdx];
      setDrag((prev) =>
        prev
          ? {
              ...prev,
              deltaHours,
              // Only a "move" drag reassigns the vehicle; resizing keeps the same row.
              previewVehicleId:
                prev.mode === "move"
                  ? targetVehicle?.id ?? prev.previewVehicleId
                  : prev.previewVehicleId,
            }
          : null,
      );
    },
    [drag, vehicleIndexFromY, vehicles],
  );

  const handlePointerUp = useCallback(() => {
    if (!drag) return;
    if (!dragMoved.current) {
      // No significant movement — treat as click, open modal
      const r = reservations.find((r) => r.id === drag.reservationId);
      if (r) setEditingReservation(r);
      setDrag(null);
      return;
    }
    const result = applyDrag(drag);
    updateReservation(drag.reservationId, result);
    setDrag(null);
  }, [drag, updateReservation, reservations]);

  // Get preview overrides for a dragged reservation
  const getPreviewOverrides = useCallback(
    (drag: DragState) => {
      const result = applyDrag(drag);
      return {
        startHour: result.startHour,
        returnHour: result.returnHour,
        durationHours: result.durationHours,
      };
    },
    [],
  );

  // Time label for bar
  const getTimeLabel = (r: VehicleReservation, overrides?: { startHour?: number; returnHour?: number; durationHours?: number }) => {
    const dayType = getDayType(r);
    const sh = overrides?.startHour ?? r.startHour;
    const rh = overrides?.returnHour ?? r.returnHour;
    const dh = overrides?.durationHours ?? r.durationHours;

    if (dayType === "single" && sh != null && dh != null) {
      return `${formatTime(sh)}–${formatTime(sh + dh)}`;
    }
    if (dayType === "first" && sh != null) {
      return `${formatTime(sh)}→`;
    }
    if (dayType === "last" && rh != null) {
      return `→${formatTime(rh)}`;
    }
    return null;
  };

  const highlightedId = useVehiculesStore((s) => s.highlightedReservationId);

  // Render a reservation bar
  const renderBar = (r: VehicleReservation, isDragPreview = false) => {
    const isDragging = drag?.reservationId === r.id;
    const isHighlighted = !isDragPreview && highlightedId === r.id;
    const dayType = getDayType(r);
    const canDrag = !(dayType === "middle" && r.startHour == null && r.returnHour == null);

    const overrides = isDragPreview && drag ? getPreviewOverrides(drag) : undefined;
    const { left, width } = getBarPx(r, overrides);
    const timeLabel = getTimeLabel(r, overrides);

    // Right-edge resize handle: single → duration, last → returnHour
    const showResize = !isDragPreview && (dayType === "single" || dayType === "last");
    // Left-edge resize handle: single → start (+ duration), first → start hour on day 1
    const showResizeStart = !isDragPreview && (dayType === "single" || dayType === "first");

    return (
      <div
        key={r.id + (isDragPreview ? "-preview" : "")}
        data-reservation-id={r.id}
        className={`absolute flex flex-row items-center${isHighlighted ? " animate-pulse" : ""}`}
        style={{
          top: 2,
          bottom: 2,
          left,
          width,
          background: isDragPreview ? "rgba(202, 207, 255, 0.6)" : isHighlighted ? "#A5B4FC" : "#CACFFF",
          border: isDragPreview ? "2px dashed #5C6BFF" : isHighlighted ? "2px solid #6366f1" : "1px solid #B7BDFF",
          boxShadow: isDragPreview ? "none" : isHighlighted ? "0px 0px 12px rgba(99, 102, 241, 0.5)" : "0px 2px 4px rgba(0, 0, 0, 0.15)",
          borderRadius: 5,
          zIndex: isDragPreview ? 30 : isHighlighted ? 20 : 10,
          cursor: canDrag ? "grab" : "pointer",
          overflow: "hidden",
          opacity: isDragging && !isDragPreview ? 0.3 : 1,
          pointerEvents: isDragPreview ? "none" : "auto",
        }}
        onPointerDown={
          !isDragPreview
            ? (e) => handlePointerDown(e, r, "move")
            : undefined
        }
      >
        {showResizeStart && (
          <div
            style={{
              width: 6,
              cursor: "ew-resize",
              flexShrink: 0,
              alignSelf: "stretch",
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
              handlePointerDown(e, r, "resize-start");
            }}
          />
        )}
        <div
          className="flex flex-row items-center flex-1"
          style={{ padding: "0 6px", minWidth: 0, overflow: "hidden" }}
        >
          <span
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: 11,
              lineHeight: "100%",
              color: "#5C6BFF",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: 1,
            }}
          >
            {r.clientName}
            {timeLabel && (
              <span style={{ color: "#8B93FF", marginLeft: 4, fontSize: 10 }}>
                {timeLabel}
              </span>
            )}
          </span>
        </div>
        {showResize && (
          <div
            style={{
              width: 6,
              cursor: "ew-resize",
              flexShrink: 0,
              alignSelf: "stretch",
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
              handlePointerDown(e, r, "resize");
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div
      className="flex flex-row"
      style={{
        border: "1px solid #D9D9D9",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      {/* Left: vehicle labels */}
      <div style={{ width: VEHICLE_COL_WIDTH, flexShrink: 0 }}>
        <div
          style={{
            height: HEADER_HEIGHT,
            borderRight: "1px solid #D9D9D9",
            borderBottom: "1px solid #D9D9D9",
          }}
        />
        {vehicles.map((vehicle) => {
          const colors = USAGE_COLORS[vehicle.usage] || USAGE_COLORS.pret;
          return (
            <div
              key={vehicle.id}
              className="flex flex-row items-center gap-1.5 cursor-pointer hover:bg-neutral-50 transition-colors"
              style={{
                height: ROW_HEIGHT,
                padding: "0 6px",
                borderRight: "1px solid #D9D9D9",
                borderBottom: "1px solid #D9D9D9",
              }}
              onClick={() => setEditingVehicle(vehicle)}
            >
              <div
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: colors.dot,
                  flexShrink: 0,
                }}
              />
              <div className="flex flex-col min-w-0" style={{ gap: 1 }}>
                <span
                  style={{
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    fontSize: 11,
                    lineHeight: "130%",
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {vehicle.name}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    fontSize: 10,
                    lineHeight: "120%",
                    color: "#9CA3AF",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {vehicle.plate}{vehicle.km > 0 ? ` / ${vehicle.km.toLocaleString("fr-FR")} km` : ""}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Right: time grid */}
      <div
        ref={containerRef}
        className="flex-1 relative"
        style={{ minWidth: 0, overflowX: "auto" }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <div style={{ width: GRID_WIDTH, position: "relative" }}>
          {/* Header: hour labels */}
          <div className="flex flex-row" style={{ height: HEADER_HEIGHT }}>
            {hours.map((h) => (
              <div
                key={h}
                className="flex items-center justify-center"
                style={{
                  width: HOUR_WIDTH,
                  height: HEADER_HEIGHT,
                  borderRight: "1px solid #D9D9D9",
                  borderBottom: "1px solid #D9D9D9",
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: 11,
                  color: "#A1A1A1",
                }}
              >
                {formatTime(h)}
              </div>
            ))}
          </div>

          {/* Vehicle rows */}
          {vehicles.map((vehicle) => {
            const vReservations = resByVehicle[vehicle.id] || [];
            return (
              <div
                key={vehicle.id}
                className="flex flex-row relative"
                style={{ height: ROW_HEIGHT }}
              >
                {/* Background quarter-hour cells */}
                {hours.map((h) => (
                  <div
                    key={h}
                    className="flex flex-row"
                    style={{ width: HOUR_WIDTH, height: ROW_HEIGHT }}
                  >
                    {[0, 1, 2, 3].map((q) => (
                      <div
                        key={q}
                        style={{
                          width: QUARTER_WIDTH,
                          height: ROW_HEIGHT,
                          borderRight:
                            q === 3
                              ? "1px solid #D9D9D9"
                              : "1px solid #F0F0F0",
                          borderBottom: "1px solid #D9D9D9",
                        }}
                      />
                    ))}
                  </div>
                ))}

                {/* Reservation bars */}
                {vReservations.map((r) => renderBar(r, false))}

                {/* Ghost preview if dragging TO this row */}
                {drag &&
                  drag.previewVehicleId === vehicle.id &&
                  (() => {
                    const origR = dayReservations.find(
                      (r) => r.id === drag.reservationId,
                    );
                    if (!origR) return null;
                    return renderBar(origR, true);
                  })()}
              </div>
            );
          })}
        </div>
      </div>

      {editingReservation && (
        <ReservationModal
          reservation={editingReservation}
          vehicles={allVehicles}
          onClose={() => setEditingReservation(null)}
          onSave={(updated) => {
            updateReservation(editingReservation.id, updated);
            setEditingReservation(null);
          }}
          onDelete={(id) => {
            deleteReservation(id);
            setEditingReservation(null);
          }}
        />
      )}

      {editingVehicle && (
        <VehiculeModal
          vehicle={editingVehicle}
          onClose={() => setEditingVehicle(null)}
          onSave={(updated) => {
            updateVehicle(updated.id, updated);
            setEditingVehicle(null);
          }}
        />
      )}
    </div>
  );
}
